import React, {Fragment, useCallback, useEffect, useMemo, useState, useContext} from 'react';
import classnames from 'classnames';
import {Tabs, Row, Col, Breadcrumb, Tooltip, Typography, Badge, Button, Drawer, Switch, Space, Collapse} from 'antd';
import {useRouteMatch, Link} from 'react-router-dom';
import {extract} from '../../utils/openapi-extractor';
import {EyeOutlined, CodeOutlined, MacCommandOutlined, PlusOutlined} from '@ant-design/icons';
import {MarkdownBlock} from '../markdown-block';
import {HeaderBlock} from '../header-block';
import {TechnicalEditor} from '../technical-editor';
import {Block} from '../block';
import {set, unset, merge} from 'lodash';
import {ParametersBlock} from './parameters-block';
import {OperationBlock} from './operation-block';
import {OpenApiContext} from '../../openapi-context';

import {AppContext} from '../../state';
import {LintView} from '../lint-view';
import {InterfaceHeaderBlock} from './interface-header-block';
import {errorsForInterface} from '../../utils/openapi-schema-validator';

// Per-verb config driving the tab bar. Order matches OpenAPI's PathItem
// property order (get, put, post, delete, options, head, patch, trace).
// Colors reuse the antd preset keys that our legacy OperationBlock already
// consumes, so existing internal rendering (method chip on the GET banner,
// etc.) stays consistent.
const VERBS = [
    {key: 'get', color: 'green'},
    {key: 'post', color: 'volcano'},
    {key: 'put', color: 'orange'},
    {key: 'patch', color: 'orange'},
    {key: 'delete', color: 'geekblue'},
    {key: 'head', color: 'cyan'},
    {key: 'options', color: 'lime'},
    {key: 'trace', color: 'blue'},
];

function VerbTabLabel({verb, defined}) {
    return (
        <span className={classnames('verbTabLabel', `verb-${verb.key}`, {'is-defined': defined})}>
            {verb.key}
        </span>
    );
}

function AddVerbOpPanel({verb, onAdd, canEdit}) {
    if (!canEdit) {
        return <Typography.Text type="secondary">No {verb.key.toUpperCase()} operation defined on this path.</Typography.Text>;
    }
    return (
        <div className="addVerbOpPanel">
            <Typography.Text type="secondary" className="addVerbOpPanel__hint">
                No {verb.key.toUpperCase()} operation yet on this path.
            </Typography.Text>
            <Button className="addVerbOpPanel__btn" type="primary" size="large" icon={<PlusOutlined />} onClick={onAdd}>
                {verb.key.toUpperCase()} Operation
            </Button>
        </div>
    );
}

function PathBar({path, paramCount, onOpenParams}) {
    return (
        <div className="interfacePathBar">
            <Typography.Text code className="interfacePathBar__path">
                {path}
            </Typography.Text>
            <Button className="interfacePathBar__paramsBtn" onClick={onOpenParams}>
                Path Params{paramCount ? ` (${paramCount})` : ''}
            </Button>
        </div>
    );
}

export function InterfaceView({spec, className, dispatch, rootPath, mobile, source = 'paths'}) {
    const app = useContext(AppContext);
    const {state, SUPPORTED_EDITOR_ROLES} = useContext(OpenApiContext);
    const classes = classnames('interfaceViewer', className);
    // source === 'paths'    -> classic interface, route segment is 'interfaces'
    // source === 'webhooks' -> OpenAPI 3.1 webhook, route segment is 'webhooks'
    // Only the route-pattern and the data-lookup path differ; rendering is
    // identical because a webhook is a PathItem structurally.
    const routeSegment = source === 'webhooks' ? 'webhooks' : 'interfaces';
    const {params} = useRouteMatch(`/:env/standards/:category/:key/:version/${routeSegment}/:intfKey`);
    const [intf, updateIntf] = useState();
    const [errorCount, setErrorCount] = useState(0);
    const [activeVerb, setActiveVerb] = useState('get');
    const [pathParamsOpen, setPathParamsOpen] = useState(false);

    const canEdit = useCallback(
        () => SUPPORTED_EDITOR_ROLES.indexOf(state.role) !== -1 && !mobile,
        [SUPPORTED_EDITOR_ROLES, state.role, mobile],
    );

    // Insert a default verb skeleton when user clicks "+ VERB Operation"
    // inside an empty verb tab. The shape matches what the old "Add" menu
    // produced so the downstream OperationBlock handles it unchanged.
    const handleAddVerb = useCallback(
        (verbKey) => {
            if (!intf) return;
            intf.spec[verbKey] = {
                responses: {
                    200: {description: 'Success', content: {'application/json': {schema: {type: 'object', properties: {}}}}},
                    400: {description: 'Bad Request', content: {'application/json': {schema: {type: 'object', properties: {}}}}},
                    500: {description: 'Server Error', content: {'application/json': {schema: {type: 'object', properties: {}}}}},
                },
            };
            dispatch({type: 'update-interface', intf});
            setActiveVerb(verbKey);
        },
        [intf, dispatch],
    );

    useEffect(() => {
        const extracted = extract(spec);
        const lookup = source === 'webhooks' ? extracted.webhook : extracted.interface;
        const found = lookup.call(extracted, params.intfKey);
        // Stamp source onto the intf record so every downstream dispatch
        // ({type: 'update-interface', intf}) carries it. The library.js
        // reducer routes to draft.paths vs draft.webhooks accordingly.
        if (found) found.source = source;
        updateIntf(found);
        app.dispatch({
            type: 'add-breadcrumb-entry',
            entry: {
                env: params.env,
                linkType: routeSegment,
                category: params.category,
                key: params.key,
                version: params.version,
                intfKey: params.intfKey,
            },
        });
    }, [spec, params.intfKey]);

    // Default the active verb tab to the first defined verb when landing
    // on a new interface. Keeps the view useful on read-only specs.
    useEffect(() => {
        if (!intf || !intf.spec) return;
        const firstDefined = VERBS.find((v) => intf.spec[v.key]);
        if (firstDefined) setActiveVerb(firstDefined.key);
    }, [intf]);

    useEffect(() => {
        if (intf) {
            setErrorCount(errorsForInterface(state.specValidationErrors, intf.path).length);
        } else {
            setErrorCount(0);
        }
    }, [spec, params.intfKey, intf, state.specValidationErrors]);

    function onSave(field) {
        return function (source) {
            const nextState = {};
            set(nextState, field, source);
            merge(intf, nextState);
            dispatch({type: 'update-interface', intf});
        };
    }

    function handleChangeSet(changeSet) {
        if (changeSet.op === 'remove-block') {
            unset(intf.spec, changeSet.name);
        } else {
            Object.keys(changeSet).map((name) => set(intf.spec, name, changeSet[name]));
        }
        dispatch({type: 'update-interface', intf});
    }

    function onTechnicalEdit(newIntf) {
        dispatch({type: 'update-interface', intf: {path: intf.path, name: intf.name, key: intf.key, source, spec: newIntf}});
    }

    const setTag = (value) => set(intf.spec, 'tags', value);

    // INTERNAL toggle: writes a custom x-internal extension on the path item.
    // Used later for publish gating (don't expose "internal" paths in the
    // public docs view).
    const isInternal = !!(intf && intf.spec && intf.spec['x-internal']);
    const toggleInternal = (checked) => {
        if (!intf) return;
        if (checked) intf.spec['x-internal'] = true;
        else unset(intf.spec, 'x-internal');
        dispatch({type: 'update-interface', intf});
    };

    const pathParamCount = useMemo(
        () => (intf && Array.isArray(intf.spec.parameters) ? intf.spec.parameters.length : 0),
        [intf],
    );

    const verbTabItems = useMemo(() => {
        if (!intf) return [];
        // View-mode (Subscriber / read-only viewer): only render tabs for
        // verbs that are actually defined on this path. Editor role still
        // sees all 8 so they can add new operations via the empty-state
        // panel — that's how new verbs get created today.
        const showAll = canEdit();
        return VERBS
            .filter((verb) => showAll || !!intf.spec[verb.key])
            .map((verb) => {
                const op = intf.spec[verb.key];
                const defined = !!op;
                return {
                    key: verb.key,
                    label: <VerbTabLabel verb={verb} defined={defined} />,
                    children: defined ? (
                        <OperationBlock
                            op={op}
                            path={intf.path}
                            name={verb.key}
                            color={verb.color}
                            params={params}
                            onSave={handleChangeSet}
                            imageRootPath={rootPath}
                            mobile={mobile}
                        />
                    ) : (
                        <AddVerbOpPanel verb={verb} onAdd={() => handleAddVerb(verb.key)} canEdit={canEdit()} />
                    ),
                };
            });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [intf, params, rootPath, mobile, handleAddVerb, canEdit]);

    // Mirror SchemaBlock's three-section collapsible layout for the
    // visual interface tab so model / API / webhook share the same
    // Description / <body> / Footer chrome. The body section here is
    // Operations rather than Properties; the count is the number of
    // verbs defined on the path (GET + POST = 2, etc).
    const definedVerbCount = useMemo(() => {
        if (!intf) return 0;
        return VERBS.filter((v) => intf.spec[v.key]).length;
    }, [intf]);

    const hasFooter = !!(intf && intf.spec && intf.spec['x-footer']);
    const hasDescription = !!(intf && intf.spec && intf.spec.description);
    const interfaceDefaultActiveKey = useMemo(() => {
        const keys = ['operations'];
        if (hasDescription) keys.unshift('description');
        if (hasFooter) keys.push('footer');
        return keys;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [hasDescription, hasFooter, params.intfKey]);

    return intf ? (
        <Fragment>
            <Row>
                <Col xs={24}>
                    <div className="breadcrumbContainer">
                        <Breadcrumb separator=">">
                            {app.state.breadcrumb.length > 1 &&
                                app.state.breadcrumb.map((entry) => (
                                    <Breadcrumb.Item key={entry.url}>
                                        <Tooltip title={`Jump to ${entry.category}/${entry.key}`}>
                                            <Link to={entry.url}>
                                                {entry.title}${entry.version}
                                            </Link>
                                        </Tooltip>
                                    </Breadcrumb.Item>
                                ))}
                        </Breadcrumb>
                    </div>
                </Col>
            </Row>
            <Row>
                <Col xs={24}>
                    <div className={classes}>
                        <Tabs
                            type="card"
                            tabPlacement="right"
                            id="modelTabs"
                            items={[
                                {
                                    key: 'visual',
                                    label: <EyeOutlined />,
                                    children: (
                                        <div className="scrollableViewer">
                                            <div className="interfaceTitleBar">
                                                <Typography.Title level={3} ellipsis className="interfaceTitleBar__title">
                                                    {intf.name}
                                                </Typography.Title>
                                                {canEdit() && (
                                                    <Space className="interfaceTitleBar__internal">
                                                        <Typography.Text type="secondary">INTERNAL</Typography.Text>
                                                        <Tooltip title="Mark this operation group as internal (excluded from public docs)">
                                                            <Switch size="small" checked={isInternal} onChange={toggleInternal} />
                                                        </Tooltip>
                                                    </Space>
                                                )}
                                            </div>

                                            <PathBar path={intf.path} paramCount={pathParamCount} onOpenParams={() => setPathParamsOpen(true)} />

                                            <Collapse
                                                className="interfaceSections"
                                                defaultActiveKey={interfaceDefaultActiveKey}
                                                items={[
                                                    {
                                                        key: 'description',
                                                        label: 'Description',
                                                        children: (
                                                            <InterfaceHeaderBlock
                                                                canEdit={canEdit}
                                                                intf={intf}
                                                                setTag={setTag}
                                                                onSave={handleChangeSet}
                                                                rootPath={rootPath}
                                                                mobile={mobile}
                                                            />
                                                        ),
                                                    },
                                                    {
                                                        key: 'operations',
                                                        label: `Operations${definedVerbCount > 0 ? ` (${definedVerbCount})` : ''}`,
                                                        children: (
                                                            <Tabs
                                                                className="verbTabs"
                                                                activeKey={activeVerb}
                                                                onChange={setActiveVerb}
                                                                items={verbTabItems}
                                                            />
                                                        ),
                                                    },
                                                    {
                                                        key: 'footer',
                                                        label: 'Footer',
                                                        children: (
                                                            <MarkdownBlock
                                                                source={intf.spec['x-footer']}
                                                                onSave={onSave('spec.x-footer')}
                                                                placeholder={
                                                                    <span>
                                                                        <strong>x-footer</strong>. Optional extended field applying to all operations in this path
                                                                    </span>
                                                                }
                                                                imageRootPath={rootPath}
                                                            />
                                                        ),
                                                    },
                                                ]}
                                            />

                                            <Drawer
                                                title="Path Parameters"
                                                placement="right"
                                                open={pathParamsOpen}
                                                onClose={() => setPathParamsOpen(false)}
                                                width={560}
                                                destroyOnHidden>
                                                <ParametersBlock parameters={intf.spec.parameters} onSave={handleChangeSet} mobile={mobile} />
                                            </Drawer>
                                        </div>
                                    ),
                                },
                                {
                                    key: 'technical',
                                    label: <CodeOutlined />,
                                    disabled: state.editMode,
                                    children: (
                                        <>
                                            <HeaderBlock>
                                                <Typography.Title level={3}>{intf.name}</Typography.Title>
                                            </HeaderBlock>
                                            <Block noEdit>
                                                <TechnicalEditor object={intf.spec} update={onTechnicalEdit} mobile={mobile} />
                                            </Block>
                                        </>
                                    ),
                                },
                                {
                                    key: 'lint',
                                    label: errorCount > 0 ? (
                                        <Badge count={errorCount}>
                                            <MacCommandOutlined />
                                        </Badge>
                                    ) : (
                                        <MacCommandOutlined />
                                    ),
                                    disabled: state.editMode,
                                    children: (
                                        <Block noEdit>
                                            <LintView spec={spec} type="interface" intKey={intf.path} />
                                        </Block>
                                    ),
                                },
                            ]}
                        />
                    </div>
                </Col>
            </Row>
        </Fragment>
    ) : null;
}
